import { createId } from "@paralleldrive/cuid2";
import { ContentStatus } from "./topics";
export class Curriculum {
    id;
    title;
    description;
    status;
    lessons;
    modules;
    constructor(data) {
        this.id = createId();
        this.title = data.title;
        this.description = data.description;
        this.status = data.status || ContentStatus.Draft;
        this.lessons = data.lessons || [];
        this.modules = data.modules || [];
    }
    update(data) {
        if (data.title !== undefined)
            this.title = data.title;
        if (data.description !== undefined)
            this.description = data.description;
        if (data.status !== undefined)
            this.status = data.status;
        if (data.lessons !== undefined)
            this.lessons = data.lessons;
        if (data.modules !== undefined)
            this.modules = data.modules;
    }
    removeEmptyLessons() {
        if (!this.lessons?.length)
            return;
        const lessonsWithTopics = this.lessons.filter((lesson) => lesson.topicId);
        this.lessons = lessonsWithTopics.length
            ? lessonsWithTopics
            : [{ topicId: null, isOptional: false }];
    }
    addLesson(topicId) {
        if (!this.lessons) {
            this.lessons = [];
        }
        else if (this.lessons.some((lesson) => lesson.topicId === topicId)) {
            return;
        }
        const emptyLessonIndex = this.lessons.findIndex((lesson) => !lesson.topicId);
        if (emptyLessonIndex >= 0) {
            this.lessons[emptyLessonIndex].topicId = topicId;
            this.lessons[emptyLessonIndex].isOptional = false;
        }
        else {
            this.lessons.push({
                topicId: topicId,
                isOptional: false,
            });
        }
    }
    addLessonAtIndex(topicId, index) {
        if (!this.lessons)
            return;
        // if index is out of range, put it in range
        if (index < 0)
            index = 0;
        if (index >= this.lessons.length)
            index = this.lessons.length - 1;
        // Find and clear any existing lesson with this topic
        const lessonWithTopicIndex = this.lessons.findIndex((lesson) => lesson.topicId === topicId);
        const lessonWithTopic = lessonWithTopicIndex !== -1 && this.lessons[lessonWithTopicIndex];
        if (lessonWithTopic) {
            lessonWithTopic.topicId = null;
        }
        // If the target lesson already has a topic, insert a new one
        if (this.lessons[index].topicId) {
            if (lessonWithTopic) {
                this.clearLessonAtIndex(lessonWithTopicIndex);
            }
            this.lessons.splice(index, 0, {
                topicId: topicId,
                isOptional: lessonWithTopic?.isOptional || false,
            });
        }
        else {
            // If the target lesson is empty, set its topic
            this.lessons[index].topicId = topicId;
            this.lessons[index].isOptional = lessonWithTopic?.isOptional || false;
        }
    }
    clearLessonAtIndex(index) {
        if (!this.lessons?.[index])
            return;
        if (!this.lessons[index].topicId) {
            if (this.lessons.length <= 1)
                return; // Don't remove if it's the last lesson
            this.lessons.splice(index, 1);
        }
        else {
            // Clear the topic from modules
            this.modules?.forEach((module) => {
                module.topicIds = module.topicIds.filter((id) => id !== this.lessons[index].topicId);
            });
            this.lessons[index].topicId = null;
        }
    }
    reorderModules() {
        if (!this.modules?.length)
            return;
        const modulesWithTopics = this.modules.filter((module) => module.topicIds?.length > 0);
        const emptyModules = this.modules.filter((module) => !module.topicIds?.length);
        this.modules = [...modulesWithTopics, ...emptyModules];
    }
    clearModuleAtIndex(index) {
        if (!this.modules?.[index])
            return;
        if (this.modules[index].topicIds.length === 0) {
            // If module is empty, remove it
            this.modules.splice(index, 1);
        }
        else {
            // If module has topics, clear them
            this.modules[index].topicIds.forEach((topicId) => {
                this.removeTopic(topicId);
            });
            this.modules[index].topicIds = [];
        }
    }
    addTopicToModule(topicId, index) {
        if (!this.modules)
            return;
        // if index is out of range, put it in range
        if (index < 0)
            index = 0;
        if (index >= this.modules.length)
            index = this.modules.length - 1;
        // Remove topic from other modules if present
        this.modules.forEach((module) => {
            if (module.topicIds) {
                module.topicIds = module.topicIds.filter((id) => id !== topicId);
            }
        });
        // Add topic to target module
        if (!this.modules[index])
            return;
        if (!this.modules[index].topicIds) {
            this.modules[index].topicIds = [];
        }
        this.modules[index].topicIds.push(topicId);
        // Add to lessons if not already present
        this.addLesson(topicId);
    }
    removeTopic(topicId) {
        // Remove from lessons
        if (this.lessons) {
            this.lessons = this.lessons.filter((lesson) => lesson.topicId !== topicId);
        }
        // Remove from modules
        if (this.modules) {
            this.modules = this.modules.map((module) => ({
                ...module,
                topicIds: module.topicIds.filter((id) => id !== topicId),
            }));
        }
    }
}
